import React, { useCallback } from 'react'
import { Link } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import allRoute from '../route/route'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { useDialogHandleClick, useRequestFromSearch } from '../../../utils/default/ComplexFlowHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { searchToRequest } from '../../../views/admin/manager/ManagerAccountPage'

interface PropTypes {
  id: number
  account: string
  nickname: string
  reload: () => void
  isAuthActive: boolean
}

const ButtonsBase: React.FC<{
  id: number
  handleDelete: () => void
  handleResetAuth: () => void
  isAuthActive: boolean
}> = React.memo((props) => {
  const {
    id,
    handleDelete,
    handleResetAuth,
    isAuthActive
  } = props
  const classes = useCommonStyles()
  const { t } = useT()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const request = useRequestFromSearch({ searchToRequest })!
  return (
    <Grid container direction="column" spacing={1} justifyContent="center">
      <Grid item>
        <Button
          component={Link}
          to={allRoute.managerAccountUpdate.encodePath({ search: request, param: { id } })}
          size="small"
          className={classes.blueGradualOutlineButton}
        >
          {t('common.update')}
        </Button>
      </Grid>
      <Grid item>
        <Button
          onClick={handleDelete}
          size="small"
          className={classes.pinkGradualOutlineButton}
        >
          {t('common.delete')}
        </Button>
      </Grid>
      <Grid item>
        <Button
          onClick={handleResetAuth}
          size="small"
          className={classes.yellowGradualOutlineButton}
          disabled={!isAuthActive}
          classes={{ disabled: classes.yellowGradualOutlineDisabledButton }}
        >
          {t('common.resetAuthenticator')}
        </Button>
      </Grid>
    </Grid>
  )
})

const ManagerAccountButtons: React.FC<PropTypes> = (props) => {
  const { id, account, nickname, reload, isAuthActive } = props
  const classes = useCommonStyles()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()
  const { t } = useT()

  const handleDelete = useDialogHandleClick({
    dialogId: `deleteAccount-${id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: (
        <div className={classes.pre} style={{ textAlign: 'center' }}>
          {`${t('dialog.confirmDelete')}\n`}
          <span className={classes.purpleWord}>[{account}] [{nickname}]</span>
        </div>
      )
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.deleteAccountSuccess')
    }),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    payload: id,
    gdkFunc: (payload) => gdk.admin.deleteAccount(payload),
    gdkFuncDependencies: [gdk],
    onSuccess: () => { reload() },
    onSuccessDependencies: [reload]
  })

  const handleResetAuth = useDialogHandleClick({
    dialogId: `resetAccountCode-${id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: (
        <div className={classes.pre} style={{ textAlign: 'center' }}>
          {`${t('dialog.confirmResetGoogleAuthenticator')}\n`}
          <span className={classes.purpleWord}>[{account}] [{nickname}]</span>
        </div>
      )
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.resetGoogleAuthenticatorSuccess')
    }),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    payload: id,
    gdkFunc: (payload) => gdk.auth.deleteAuthenticator(payload),
    gdkFuncDependencies: [gdk],
    onSuccess: () => { reload() },
    onSuccessDependencies: [reload]
  })

  return (
    <ButtonsBase
      id={id}
      isAuthActive={isAuthActive}
      handleDelete={handleDelete}
      handleResetAuth={handleResetAuth}
    />
  )
}

export default React.memo(ManagerAccountButtons)
